import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, StatusBadge } from '@/components/common'
import { Cpu, Activity, AlertTriangle, CheckCircle, ExternalLink } from 'lucide-react'
import { assetApi, AssetSummary } from '@/services/assetApi'
import clsx from 'clsx'

interface FleetOverviewProps {
  loading?: boolean
}

export default function FleetOverview({ loading: externalLoading = false }: FleetOverviewProps) {
  const navigate = useNavigate()
  const [assets, setAssets] = useState<AssetSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [isFromAPI, setIsFromAPI] = useState(false)

  // Fetch assets on mount
  useEffect(() => {
    const fetchAssets = async () => {
      setLoading(true)
      try {
        const result = await assetApi.getAssetsWithFallback()
        // Guard against undefined data
        const assetsData = Array.isArray(result?.data) ? result.data : []
        setAssets(assetsData)
        setIsFromAPI(!result?.isMock)
      } catch (error) {
        console.error('Error fetching assets:', error)
        setAssets([]) // Set empty array on error
      } finally {
        setLoading(false)
      }
    }

    fetchAssets()
  }, [])

  // Sort by health score (lowest first)
  const sortedAssets = [...assets].sort((a, b) => a.health_score - b.health_score)

  // Navigate to asset detail page
  const handleAssetClick = (assetId: string) => {
    navigate(`/assets/${assetId}`)
  }

  // Navigate to full asset list
  const handleViewAllClick = () => {
    navigate('/assets')
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'critical':
        return <AlertTriangle size={14} className="text-status-critical" />
      case 'warning':
        return <AlertTriangle size={14} className="text-status-warning" />
      case 'healthy':
        return <CheckCircle size={14} className="text-status-healthy" />
      default:
        return <Activity size={14} className="text-surface-500" />
    }
  }

  const getHealthColor = (score: number) => {
    if (score >= 85) return 'bg-status-healthy'
    if (score >= 60) return 'bg-status-warning'
    return 'bg-status-critical'
  }

  if (loading || externalLoading) {
    return (
      <Card title="Fleet Overview">
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-12 bg-surface-700 rounded animate-pulse" />
          ))}
        </div>
      </Card>
    )
  }

  const isMockData = !isFromAPI

  return (
    <Card
      title={isMockData ? 'Fleet Overview [SIMULATED]' : 'Fleet Overview'}
      subtitle={isMockData ? 'Mock data mode enabled - disable in settings for live data' : 'Assets ranked by health score'}
    >
      <div className="space-y-2">
        {sortedAssets.map((asset) => (
          <div
            key={asset.id}
            onClick={() => handleAssetClick(asset.id)}
            className="flex items-center gap-3 p-3 bg-surface-900 rounded-lg hover:bg-surface-800 transition-colors cursor-pointer group"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleAssetClick(asset.id)}
          >
            {/* Icon */}
            <div className="flex-shrink-0">
              <Cpu size={18} className="text-surface-500 group-hover:text-primary-400 transition-colors" />
            </div>

            {/* Asset Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm text-primary-400 group-hover:text-primary-300 transition-colors">
                  {asset.id}
                </span>
                {getStatusIcon(asset.status)}
              </div>
              <p className="text-xs text-surface-500 truncate">{asset.name}</p>
            </div>

            {/* Health Score Bar */}
            <div className="flex-shrink-0 w-24">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-surface-700 rounded-full overflow-hidden">
                  <div
                    className={clsx('h-full rounded-full transition-all', getHealthColor(asset.health_score))}
                    style={{ width: `${asset.health_score}%` }}
                  />
                </div>
                <span className={clsx(
                  'text-xs font-mono font-semibold w-8 text-right',
                  asset.health_score >= 85 ? 'text-status-healthy' :
                  asset.health_score >= 60 ? 'text-status-warning' :
                  'text-status-critical'
                )}>
                  {asset.health_score}
                </span>
              </div>
            </div>

            {/* Status Badge */}
            <div className="flex-shrink-0 flex items-center gap-2">
              <StatusBadge status={asset.status} size="sm" showDot={false} />
              <ExternalLink size={14} className="text-surface-600 group-hover:text-primary-400 transition-colors" />
            </div>
          </div>
        ))}
      </div>

      {/* View All Link */}
      <div className="mt-4 text-center">
        <button
          onClick={handleViewAllClick}
          className="text-sm text-primary-400 hover:text-primary-300 transition-colors flex items-center gap-2 mx-auto"
        >
          View All Assets ({assets.length})
          <ExternalLink size={14} />
        </button>
      </div>
    </Card>
  )
}
